import React, { useState } from "react";
import { ImageWithShimmer } from "./admin/ProjectShowcaseList";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteUserModal from "../../components/formComponents/DeleteUserModal";
import axios from "axios";
import useRole from "../../utils/useRole";
import { getMinioSize, getUpdatedImageUrlWithSize } from "../../utils/helpers";

const ProjectCard = ({
  item,
  allProjects,
  isViewOnly,
  isEqualHeight,
  setProjectData,
}) => {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const { isSuperAdmin, isAdmin } = useRole();

  if (deleted) return null;

  const openDeleteModal = (e) => {
    e.stopPropagation();
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    setDelLoading(true);
    try {
      const token = localStorage.getItem("usersdatatoken");
      await axios.delete(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/${item._id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );
      toast.success("Project deleted successfully");
      setDeleted(true);
    } catch (error) {
      console.error(
        "Error deleting project:",
        error.response ? error.response.data : error,
      );
      toast.error("Something went wrong...");
    } finally {
      closeDeleteModal();
      setDelLoading(false);
    }
  };
  const handlePinProject = async (id) => {
    try {
      const token = localStorage.getItem("usersdatatoken");
      const response = await axios.patch(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/toggle-pin/${id}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );
      setProjectData((prev) => {
        let pinnedProject = prev.filter((p) => p._id == id);
        pinnedProject[0].isPinned = !pinnedProject[0].isPinned;

        const others = prev.filter((p) => p._id !== id);
        return [...(pinnedProject || []), ...(others || [])].sort((a, b) => {
          if (b.isPinned !== a.isPinned) {
            return b.isPinned - a.isPinned;
          }
          if ((a.sortingOrder ?? 0) !== (b.sortingOrder ?? 0)) {
            return (a.sortingOrder ?? 0) - (b.sortingOrder ?? 0);
          }
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
      });

      toast.success("Project pinned successfully");
    } catch (error) {
      console.error(
        "Error pinning project:",
        error.response ? error.response.data : error,
      );
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong !!!",
      );
    }
  };
  const handleToggleFavorite = async (e) => {
    e?.stopPropagation();
    const newFavoriteStatus = !item?.isFavorite;
    const previousProjects = allProjects;

    // Optimistic update
    if (setProjectData) {
      setProjectData((prev) =>
        prev.map((p) =>
          p._id === item._id ? { ...p, isFavorite: newFavoriteStatus } : p,
        ),
      );
    }

    try {
      const token = localStorage.getItem("usersdatatoken");
      await axios.put(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/${item._id}`,
        { isFavorite: newFavoriteStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );
      toast.success(
        newFavoriteStatus ? "Added to favorites" : "Removed from favorites",
      );
    } catch (error) {
      console.error("Error updating favorite status:", error);
      if (setProjectData && previousProjects) {
        setProjectData(previousProjects);
      }
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update favorite status",
      );
    }
  };

  return (
    <>
      <div
        onClick={() =>
          navigate(`/project-showcase/${item._id}`, { state: { allProjects } })
        }
        className={`relative group rounded-md overflow-hidden border w-full ${isEqualHeight ? "h-[240px]" : "h-auto"}`}
      >
        {!isViewOnly && (
          <button
            type="button"
            onClick={handleToggleFavorite}
            className={`size-8 grid place-content-center rounded-full backdrop-blur-md transition-all shadow-md absolute top-2.5 right-2.5 z-[10] ${
              item?.isFavorite
                ? "bg-amber-400/90 text-white hover:bg-amber-500 scale-105"
                : "bg-black/40 text-white/80 hover:bg-black/60 hover:text-white"
            }`}
            title={
              item?.isFavorite ? "Remove from Favorites" : "Mark as Favorite"
            }
          >
            <Icon
              icon={
                item?.isFavorite
                  ? "material-symbols:star-rounded"
                  : "material-symbols:star-outline-rounded"
              }
              className="text-xl"
            />
          </button>
        )}
        <div className="rounded-xl z-[8] cursor-pointer absolute from-black/80 to-transparent bg-gradient-to-t inset-x-0  -bottom-2 pt-30 text-white flex items-end">
          <div className="w-full ">
            <div className="p-4 text-xl pb-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-bold break-words line-clamp-2">
                    {item?.title}
                  </h2>
                  <p className="opacity-60 text-sm ">
                    {Number(item?.spaceSize).toLocaleString()} Sqft
                    <br />
                    <span>
                      {[
                        item?.buildingName,
                        item?.location?.city,
                        item?.location?.state,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </p>
                </div>
                {!isViewOnly && (
                  <div className="flex gap-2 mt-1">
                    {/* Edit Icon */}
                    {(isSuperAdmin || isAdmin) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/project-showcase/${item._id}/edit/projects`,
                          );
                        }}
                        className="bg-gray-200/30 backdrop-blur-sm size-8 grid place-content-center rounded-lg text-white hover:bg-gray-200/50 transition-all"
                      >
                        <Icon
                          icon="fluent:edit-48-regular"
                          className="text-xl"
                        />
                      </button>
                    )}
                    {/* Delete Icon */}
                    {isSuperAdmin && (
                      <button
                        onClick={openDeleteModal}
                        className="bg-gray-200/30 backdrop-blur-sm size-8 grid place-content-center rounded-lg text-white hover:bg-gray-200/50 transition-all"
                      >
                        <Icon
                          icon="fluent:delete-48-regular"
                          className="text-xl"
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <ImageWithShimmer
          alt={item?.title}
          loading="lazy"
          draggable={false}
          className={`w-full object-cover bg group-hover:scale-110 block transition-all duration-300 ease ${isEqualHeight ? "h-[260px]" : "h-auto"}`}
          src={getUpdatedImageUrlWithSize(
            ` ${process.env.REACT_APP_BACK_URL}/api/minio/${item?.thumbNail}`,
            [1, 1, 2],
          )}
        />
      </div>
      <DeleteUserModal
        loading={delLoading}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
      />
    </>
  );
};

export default ProjectCard;
